import clsx from 'clsx';

import { monthShort } from '@/lib/period';

export interface MonthBar {
  month: string;
  value: number;
  /** Вторая величина внутри столбца (например, доля сейфа от оборота). */
  inner?: number;
}

/**
 * Двенадцать столбцов по месяцам. Тап выбирает месяц — подробности показывает
 * родитель под графиком (на телефоне нет hover).
 */
export default function MonthBars({
  data,
  selected,
  onSelect,
  label,
  size = 'md',
}: {
  data: MonthBar[];
  selected: string | null;
  onSelect: (month: string) => void;
  label: string;
  size?: 'md' | 'sm';
}) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div
      className={clsx('flex items-stretch gap-1 sm:gap-2', size === 'sm' ? 'h-28' : 'h-44')}
      role="group"
      aria-label={label}
    >
      {data.map((d) => {
        const active = d.month === selected;
        const height = d.value > 0 ? Math.max(3, (d.value / max) * 100) : 0;
        const inner = d.inner && d.value > 0 ? Math.min(100, (d.inner / d.value) * 100) : 0;
        return (
          <button
            key={d.month}
            type="button"
            onClick={() => onSelect(d.month)}
            aria-pressed={active}
            className="group flex min-w-0 flex-1 flex-col items-center gap-1.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange/40"
          >
            <div className="relative flex w-full flex-1 items-end justify-center">
              {d.value > 0 ? (
                <div
                  className={clsx(
                    'relative w-full max-w-[34px] overflow-hidden rounded-t-md transition-colors',
                    active ? 'bg-orange' : 'bg-orange/35 group-hover:bg-orange/55',
                  )}
                  style={{ height: `${height}%` }}
                >
                  {inner > 0 ? (
                    <div
                      className={clsx('absolute inset-x-0 bottom-0', active ? 'bg-ink/80' : 'bg-ink/30')}
                      style={{ height: `${inner}%` }}
                    />
                  ) : null}
                </div>
              ) : (
                <div className="h-[2px] w-full max-w-[34px] rounded-full bg-line" />
              )}
            </div>
            <span className={clsx('text-[10px] sm:text-[11px]', active ? 'font-semibold text-ink' : 'text-ink/45')}>
              {monthShort(d.month)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
